import { fetchEventSource } from '@microsoft/fetch-event-source'
import axios from 'axios'
import type { ApiResp } from '@/types/api'
import type {
  BuybackOrderAlert,
  PendingConfirmOrderAlertItem,
} from '@/types/orderAlert'

type RawPendingConfirmOrderAlertItem = Partial<
  Omit<
    PendingConfirmOrderAlertItem,
    | 'orderId'
    | 'sellerId'
    | 'buyerId'
    | 'clientGoodsId'
    | 'salePrice'
    | 'overdueMinutes'
  >
> & {
  orderId?: number | string
  sellerId?: number | string
  buyerId?: number | string
  clientGoodsId?: number | string
  salePrice?: number | string
  overdueMinutes?: number | string
}

type RawBuybackOrderAlert = Partial<Omit<BuybackOrderAlert, 'records'>> & {
  thresholdHours?: number | string
  overdueCount?: number | string
  records?: RawPendingConfirmOrderAlertItem[]
}

export interface SubscribeOptions {
  token: string
  onAlert: (data: BuybackOrderAlert) => void
  onError?: (error: unknown) => void
}

function toNumber(value: unknown, fallback = 0) {
  const normalizedValue = Number(value)
  return Number.isFinite(normalizedValue) ? normalizedValue : fallback
}

function toText(value: unknown) {
  return value == null ? '' : String(value)
}

function normalizeRecord(
  record: RawPendingConfirmOrderAlertItem
): PendingConfirmOrderAlertItem {
  return {
    orderId: toText(record.orderId),
    orderNo: record.orderNo ?? '',
    sellerId: toText(record.sellerId),
    buyerId: toText(record.buyerId),
    clientGoodsId: toText(record.clientGoodsId),
    goodsName: record.goodsName ?? '',
    salePrice: toNumber(record.salePrice),
    remark: record.remark ?? '',
    payTime: record.payTime ?? '',
    overdueMinutes: toNumber(record.overdueMinutes),
  }
}

export function normalizeBuybackAlert(
  payload?: RawBuybackOrderAlert | null
): BuybackOrderAlert {
  const rawRecords = Array.isArray(payload?.records) ? payload?.records ?? [] : []
  const records = rawRecords.map(normalizeRecord)
  const overdueCount = toNumber(payload?.overdueCount, records.length)

  return {
    serverTime: payload?.serverTime ?? '',
    thresholdHours: toNumber(payload?.thresholdHours, 2),
    thresholdTime: payload?.thresholdTime ?? '',
    hasOverdue: overdueCount > 0,
    overdueCount,
    records,
  }
}

function getEventSourceUrl() {
  const baseUrl = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? ''
  return baseUrl
    ? `${baseUrl}/order/pending-confirm-alert/stream`
    : '/order/pending-confirm-alert/stream'
}

export async function getBuybackAlert(token?: string) {
  const requestConfig = token
    ? {
        headers: {
          Authorization: token,
        },
      }
    : undefined

  const response = (await axios.get(
    '/order/pending-confirm-alert',
    requestConfig
  )) as ApiResp<RawBuybackOrderAlert>

  return normalizeBuybackAlert(response.data)
}

export async function ignorePendingConfirmAlert(orderId: string) {
  await axios.post(`/order/pending-confirm-alert/ignore/${orderId}`)
}

export function subscribeBuybackAlert(options: SubscribeOptions) {
  const controller = new AbortController()

  fetchEventSource(getEventSourceUrl(), {
    method: 'GET',
    headers: {
      Authorization: options.token,
      Accept: 'text/event-stream',
    },
    signal: controller.signal,
    openWhenHidden: true,
    async onopen(response) {
      if (!response.ok) {
        throw new Error(`SSE 连接失败: ${response.status}`)
      }
    },
    onmessage(message) {
      if (message.event === 'heartbeat') {
        return
      }

      if (message.event === 'pending-confirm-order-alert') {
        const payload = JSON.parse(message.data) as RawBuybackOrderAlert
        options.onAlert(normalizeBuybackAlert(payload))
      }
    },
    onclose() {
      throw new Error('SSE 连接已关闭')
    },
    onerror(error) {
      options.onError?.(error)
      return 3000
    },
  }).catch((error) => {
    options.onError?.(error)
  })

  return () => controller.abort()
}
